import { DateTime } from "luxon";
import { z } from "zod";

import { useClock } from "./useClock";

export function useTextingHours(textingHours: {
  start: string;
  end: string;
  timezone: string;
}): { isInTextingHours: boolean } {
  const now = useClock();
  return {
    isInTextingHours: isDateWithinTextingHours(now.toJSDate(), textingHours),
  };
}

export function isDateWithinTextingHours(
  dateToCheck: Date,
  textingHours: { start: string; end: string; timezone: string }
): boolean {
  const dt = DateTime.fromJSDate(dateToCheck).setZone(textingHours.timezone);
  const startTime = timeOfDayOnDate(dt, textingHours.start);
  const endTime = timeOfDayOnDate(dt, textingHours.end);
  return dt >= startTime && dt <= endTime;
}

export function createScheduleForSchema(textingHours: {
  start: string;
  end: string;
  timezone: string;
}): z.ZodType<Date | null | undefined> {
  return z.coerce
    .date()
    .nullish()
    .superRefine((schedule, ctx) => {
      const addIssue = (message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, message });

      const outsideHours = !isDateWithinTextingHours(new Date(), textingHours);
      if (!schedule && outsideHours) {
        addIssue("Required when outside texting hours");
      }

      if (!schedule) {
        return;
      }

      if (schedule <= new Date()) {
        addIssue("Must be in the future");
      }

      if (!isDateWithinTextingHours(schedule, textingHours)) {
        addIssue("Must be within texting hours");
      }
    });
}

function timeOfDayOnDate(date: DateTime, timeStr: string): DateTime {
  const [hour = 0, minute = 0, second = 0] = timeStr.split(":").map(Number);
  return date.set({ hour, minute, second, millisecond: 0 });
}
